<template>
  <div v-if="total || loading" class="hdrImp" :title="'Importe ' + rangeLabel + ' · Klick wechselt Heute / 7 Tage'" @click="toggleRange">
    <!-- Farbleiste: ein Segment je Importeur, Breite = Anteil -->
    <div class="hdrImp__bar">
      <div
        v-for="seg in segments"
        :key="seg.key"
        class="hdrImp__seg"
        :style="{ width: seg.pct + '%', background: seg.color }"
        @mouseenter="hover = seg.key"
        @mouseleave="hover = null"
      >
        <div v-if="hover === seg.key" class="hdrImp__tip">
          <span class="hdrImp__dot" :style="{ background: seg.color }"></span>
          <b>{{ seg.name }}</b> · {{ seg.count }} {{ seg.count === 1 ? "Import" : "Importe" }} ({{ Math.round(seg.pct) }} %)
        </div>
      </div>
    </div>
    <span class="hdrImp__total">{{ total }}<small>{{ range === "today" ? " heute" : " · 7 T." }}</small></span>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { collection, getCountFromServer, query, where } from "firebase/firestore";
import { db } from "../../fb";

// Admin-Leiste neben der Order-Suche (Malte 26.10.2026): wer hat wie viele Tasks
// importiert — eine Farbleiste mit Anteilen, Hover zeigt Name und Anzahl.
// Zählung über tasks (editors.IMPORTED = Importeur, AUTO_IMPORT = Automatik) per
// Count-Aggregation je Nutzer; Zeitraum über importedAt (ms, Import-Zeitpunkt; für
// Alt-Tasks aus der Dokument-Erstellzeit nachgetragen).
// Braucht den Composite-Index tasks(editors.IMPORTED ASC, importedAt ASC).
const PALETTE = ["#7b5cd6", "#2e9e8f", "#e8873a", "#3f7fd6", "#d6467b", "#8fbf3f", "#b57bd6", "#e0b43a", "#4fb8c9", "#c75c5c", "#6f8f3f", "#5c6bd6"];
const AUTO = "AUTO_IMPORT";
const REFRESH_MS = 120000;

export default {
  name: "HeaderImports",
  data() {
    return { range: window.localStorage.getItem("td_imports_range") || "today", counts: {}, loading: true, hover: null, timer: null };
  },
  computed: {
    ...mapGetters(["allUsers"]),
    rangeLabel() {
      return this.range === "today" ? "heute" : "der letzten 7 Tage";
    },
    total() {
      return Object.values(this.counts).reduce((a, b) => a + b, 0);
    },
    segments() {
      const users = this.allUsers || [];
      const entries = Object.entries(this.counts).filter(([, n]) => n > 0);
      // feste Farbe je Nutzer (stabil über Zeiträume): Reihenfolge der Nutzerliste
      const colorOf = (key) => {
        if (key === AUTO) return "#c9c5d6";
        const idx = users.findIndex((u) => u.uid === key);
        return PALETTE[(idx >= 0 ? idx : key.length) % PALETTE.length];
      };
      return entries
        .map(([key, count]) => {
          const u = users.find((x) => x.uid === key);
          return { key, count, pct: (count / this.total) * 100, color: colorOf(key), name: key === AUTO ? "Auto-Import" : (u && u.username) || key.slice(0, 6) + "…" };
        })
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    },
  },
  watch: {
    allUsers() {
      this.load();
    },
    range() {
      this.load();
    },
  },
  mounted() {
    this.load();
    this.timer = setInterval(this.load, REFRESH_MS);
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  methods: {
    toggleRange() {
      this.range = this.range === "today" ? "7d" : "today";
      window.localStorage.setItem("td_imports_range", this.range);
    },
    sinceMs() {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      if (this.range === "7d") d.setDate(d.getDate() - 6);
      return d.getTime();
    },
    async load() {
      const users = this.allUsers || [];
      if (!users.length) return;
      const since = this.sinceMs();
      const keys = [AUTO, ...users.map((u) => u.uid)];
      try {
        const results = await Promise.all(
          keys.map(async (key) => {
            const q = query(collection(db, "tasks"), where("editors.IMPORTED", "==", key), where("importedAt", ">=", since));
            const snap = await getCountFromServer(q);
            return [key, snap.data().count];
          })
        );
        this.counts = Object.fromEntries(results);
      } catch (e) {
        // z. B. Index noch im Aufbau — Leiste bleibt leer, kein Fehler im UI
        console.warn("HeaderImports:", e.message);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.hdrImp {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 14px;
  height: 40px;
  cursor: pointer;
  user-select: none;

  &__bar {
    display: flex;
    width: min(260px, 22vw);
    height: 12px;
    border-radius: 999px;
    overflow: visible;
    background: #f4f3f7;
    position: relative;
  }
  &__seg {
    position: relative;
    height: 100%;
    min-width: 3px;
    transition: filter 0.15s, width 0.3s;
    &:first-child { border-radius: 999px 0 0 999px; }
    &:last-child { border-radius: 0 999px 999px 0; }
    &:only-child { border-radius: 999px; }
    &:hover { filter: brightness(1.12); }
  }
  &__tip {
    position: absolute;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
    background: #282828;
    color: #fff;
    font-size: 12px;
    padding: 5px 10px;
    border-radius: 8px;
    box-shadow: 0 6px 18px rgba(16, 24, 40, 0.25);
    z-index: 30;
    display: flex;
    align-items: center;
    gap: 6px;
    pointer-events: none;
  }
  &__dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
  &__total {
    font-size: 13px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.65);
    white-space: nowrap;
    small { font-weight: 500; color: rgba(0, 0, 0, 0.45); }
  }
}
</style>
